import React, { ChangeEvent, FC, useRef, useState, useEffect, DragEvent } from 'react';
import { Button, Image, Link, Text, Types } from '@marriott/mi-ui-library';
import { ImageType } from '@marriott/mi-groups-graphql';
import { ImageCardProps } from './ImageCard.types';
import { StyledImageCard, StyledImageCardShell } from './ImageCard.styles';
import { RESLINK_ALLOWED_IMAGE_TYPES, RESLINK_IMAGE_MAX_SIZE } from '../../../../../constants';
import { uploadFile, deleteFile } from '../../../../../../utils';
import { UploadErrorResponse } from '../../../../../../molecules/Attachments/Attachments.types';
import { ERROR_CODES } from '../../../../../../constants';
import { ImageCropper } from '../../../../../molecules';

export const ImageCard: FC<ImageCardProps> = ({
  index,
  fields,
  append,
  remove,
  placeholder,
  chooseImage,
  dragHere,
  removeAriaLabel,
  imageSizeError,
  imageFormatError,
  virusImageApiError,
  uploadImageApiError,
  removeImageApiError,
  or,
  addHotelToSeeImages,
  crop,
  cropAriaLabel,
  imageCropperPopUp,
  image: imageData,
  onError,
  enableImageCropper = false
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [image, setImage] = useState<ImageType | null>(null);
  const [imageKey, setImageKey] = useState<string>('');
  const [uploadFileLoader, setUploadFileLoader] = useState(false);
  const [deleteFileLoader, setDeleteFileLoader] = useState(false);

  const [showCropper, setShowCropper] = useState(false);

  useEffect(() => {
    if (imageData?.link) {
      setImage({ name: imageData.name || '', link: imageData.link });
      setImageKey(imageData.link);
    }
  }, [imageData]);

  const selectImage = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const validateFile = (file: File): boolean => {
    if (file.size >= RESLINK_IMAGE_MAX_SIZE) {
      onError(imageSizeError);
      return false;
    }
    if (!RESLINK_ALLOWED_IMAGE_TYPES.includes(file.type)) {
      onError(imageFormatError);
      return false;
    }
    return true;
  };

  const uploadImage = async (file: File | undefined) => {
    if (file && validateFile(file)) {
      setUploadFileLoader(true);
      const reader = new FileReader();
      reader.readAsDataURL(file);
      try {
        const response = await uploadFile(file, 'reslink');

        if (!response.ok) {
          const errorResponse = await response.json();
          throw errorResponse;
        }

        const result = await response.json();
        const { data } = result;
        append({ name: file.name, link: data?.[0].documentId || '' });
        const uploadedImage = data?.[0];
        if (uploadedImage) {
          setImage({ name: file.name, link: reader?.result as string });
          setImageKey(data?.[0].documentId);
          onError('');
        }
      } catch (error) {
        const { errors, status } = error as UploadErrorResponse;
        const errorCode = errors?.[0].code;
        if (status === ERROR_CODES.HTTP.UNPROCESSABLE_ENTITY && errorCode === ERROR_CODES.IMAGE_UPLOAD.SIZE) {
          onError(imageSizeError);
        } else if (status === ERROR_CODES.HTTP.UNPROCESSABLE_ENTITY && errorCode === ERROR_CODES.IMAGE_UPLOAD.FORMAT) {
          onError(imageFormatError);
        } else if (status === ERROR_CODES.HTTP.UNPROCESSABLE_ENTITY && errorCode === ERROR_CODES.IMAGE_UPLOAD.VIRUS) {
          onError(virusImageApiError);
        } else {
          onError(uploadImageApiError);
        }
      } finally {
        setUploadFileLoader(false);
      }
    }
  };

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    uploadImage(file);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (image?.link) {
      return;
    }

    const file = event.dataTransfer.files?.[0];
    uploadImage(file);
  };

  const handleImageDeletion = async () => {
    setDeleteFileLoader(true);
    try {
      const imageIndex = fields.findIndex(field => field.link === imageKey);
      const response = await deleteFile(imageKey, 'reslink');

      if (!response.ok) {
        const errorResponse = await response.json();
        throw errorResponse;
      }

      const result = await response.json();
      if (result.data.fileDetails?.[0]) {
        remove(imageIndex);
        setImage(null);
        onError('');
        return true;
      } else {
        onError(removeImageApiError);
        return false;
      }
    } catch (error) {
      onError(removeImageApiError);
      return false;
    } finally {
      setDeleteFileLoader(false);
    }
  };

  const uploadCroppedImage = async (newFile: File) => {
    const deleted = await handleImageDeletion();
    if (deleted) {
      await uploadImage(newFile);
    }
  };

  return (
    <StyledImageCard onDragOver={event => event.preventDefault()} onDrop={handleDrop} data-testid={'image-card'}>
      <div className="card">
        {image?.link && !uploadFileLoader ? (
          <>
            <Image title={image?.name} defaultImageURL={image.link} altText={image?.name} customClass="card-image" />
            <Button
              className="icon-clear icon-l remove-card"
              ariaLabel={removeAriaLabel}
              callback={handleImageDeletion}
            />
          </>
        ) : (
          <StyledImageCardShell>
            <Text
              element={Types.tags.paragraph}
              copyText={`${placeholder} ${index + 1}`}
              fontSize={Types.size.medium}
              customClass="mb-1"
            />
            {uploadFileLoader || deleteFileLoader ? (
              <div className="text-center mb-5">
                <div className="m-spinner-m" />
              </div>
            ) : (
              <>
                <p className="mb-2">
                  <Link linkHref="#" linkClassName="t-font-m shell-subtitle" callback={selectImage}>
                    {chooseImage}
                  </Link>
                  <Text
                    element={Types.tags.span}
                    copyText={` ${dragHere}`}
                    fontSize={Types.size.medium}
                    customClass={'t-font-m'}
                  />
                </p>
                {enableImageCropper && (
                  <>
                    <Text
                      element={Types.tags.paragraph}
                      copyText={or}
                      fontSize={Types.size.medium}
                      customClass={'t-font-m mb-2'}
                    />
                    <Text
                      element={Types.tags.paragraph}
                      copyText={addHotelToSeeImages}
                      fontSize={Types.size.medium}
                      customClass={'t-font-m text-center'}
                    />
                  </>
                )}
              </>
            )}
          </StyledImageCardShell>
        )}
        <input
          type="file"
          ref={fileInputRef}
          aria-label={`${chooseImage} ${dragHere}`}
          className="d-none"
          {...{ accept: RESLINK_ALLOWED_IMAGE_TYPES.join(',') }}
          data-testid={'image-input'}
          onChange={handleImageChange}
        />
      </div>
      {enableImageCropper && image?.link && (
        <div className="d-flex justify-content-end m-2">
          <Button
            className="m-button-primary m-button-s"
            callback={() => setShowCropper(true)}
            buttonCopy={crop}
            ariaLabel={cropAriaLabel}
          />
          <ImageCropper
            label={imageCropperPopUp}
            show={showCropper}
            image={image}
            onClose={() => setShowCropper(false)}
            onConfirm={croppedImage => uploadCroppedImage(croppedImage)}
          />
        </div>
      )}
    </StyledImageCard>
  );
};
